import store from '@state/store'

// auth related routes
const authRoutes = [
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/pages/account/login')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => lazyLoadView(import('@views/pages/account/register')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/confirm-account',
    name: 'confirm-account',
    component: () => lazyLoadView(import('@views/pages/account/confirm')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/forget-password',
    name: 'forget-password',
    component: () =>
      lazyLoadView(import('@views/pages/account/forgetPassword')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/new-password',
    name: 'new-password',
    component: () =>
        lazyLoadView(import('@views/pages/account/newPassword')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
]

// error pages
const errorPagesRoutes = [
  {
    path: '/404',
    name: '404',
    component: require('@views/pages/secondary/error-404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  {
    path: '/500',
    name: '500',
    component: require('@views/pages/secondary/error-500').default,
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// dashboard
const dashboardRoutes = [
  {
    path: '/',
    name: 'Dashboard',
    header: 'Marine System Solution',
    icon: 'home',
    badge: {
      text: '1',
      varient: 'success',
    },
    component: () => lazyLoadView(import('@views/pages/dashboard/FleetDashboard')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.profile || {} }),
  },
]

// apps
const operationalRoutes = [
  {
    path: '/ops/operational',
    name: 'Operational',
    component: () => lazyLoadView(import('@views/pages/operation/operational/')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.profile || {} }),
  },
]

const operationsRoutes = [
  {
    path: '/ops',
    name: 'Operations',
    icon: 'map',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'certificate',
        name: 'Doc. Class & Statutory',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate')),
      },
      {
        path: 'activities',
        name: 'Aktivitas',
        component: () => lazyLoadView(import('@views/pages/operation/activities')),
      },
    ],
  },
]

const technicalRoutes = [
  {
    path: '/tech',
    name: 'Technical',
    icon: 'coffee',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'maintenance',
        name: 'Maintenance',
        component: () => lazyLoadView(import('@views/pages/technical/MaintenanceSummary')),
      },
      {
        path: 'service-repair',
        name: 'Service Repair',
        component: () => lazyLoadView(import('@views/pages/operation/activities')),
      },
      {
        path: 'docking',
        name: 'Docking',
        component: () => lazyLoadView(import('@views/pages/operation/activities')),
      },
      {
        path: 'master-equipment',
        name: 'Master Equipment',
        component: () => lazyLoadView(import('@views/pages/operation/activities')),
      },
    ],
  },
]

const prlogisticRoutes = [
  {
    path: '/pr',
    name: 'PR & Logistic',
    icon: 'package',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'pr-sr',
        name: 'PR / SR',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'po-so',
        name: 'PO / SO',
        component: () => lazyLoadView(import('@views/pages/operation/activities')),
      },
      {
        path: 'do',
        name: 'Delivery Order',
        component: () => lazyLoadView(import('@views/pages/operation/activities')),
      },
      {
        path: 'inventory',
        name: 'Inventory',
        component: () => lazyLoadView(import('@views/pages/operation/activities')),
      },
    ],
  },
]

const hseRoutes = [
  {
    path: '/hse',
    name: 'HSE',
    icon: 'pocket',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'activities',
        name: 'Activities',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      }
    ],
  },
]

const crewingRoutes = [
  {
    path: '/crew',
    name: 'Crewing',
    icon: 'user-check',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'candidate',
        name: 'Crew Candidate (CV)',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'recruitment',
        name: 'Recruitment',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'official-crew',
        name: 'Official Crew',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'scheduling',
        name: 'Scheduling',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      }
    ],
  },
]

const reportRoutes = [
  {
    path: '/report',
    name: 'Reports',
    icon: 'bar-chart-2',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'management',
        name: 'Report Management',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'operations',
        name: 'Report Operations',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'technical',
        name: 'Report Technical',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'pr-logistic',
        name: 'Report PR & Logistic',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'hse',
        name: 'Report HSE',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      },
      {
        path: 'crewing',
        name: 'Report Crewing',
        component: () => lazyLoadView(import('@views/pages/certificate/certificate/')),
      }
    ],
  },
]

const settingRoutes = [
  {
    path: '/setting',
    name: 'Master & Settings',
    icon: 'archive',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'user-privilege',
        name: 'User Privilege',
        component: () => lazyLoadView(import('@views/pages/settings/UserRolePrivilege')),
      },
      {
        path: 'port-list',
        name: 'Port List',
        component: () => lazyLoadView(import('@views/pages/settings/MasterPort')),
      },
      {
        path: 'project-reason',
        name: 'Project Reason',
        component: () => lazyLoadView(import('@views/pages/settings/MasterProjectReason')),
      },
      {
        path: 'module-category',
        name: 'Module Category',
        component: () => lazyLoadView(import('@views/pages/settings/ModuleCategory')),
      },
      {
        path: 'master-power-rob',
        name: 'Power Rob',
        component: () => lazyLoadView(import('@views/pages/settings/MasterTypePower')),
      }
    ],
  },
];

const logRoutes = [
  {
    path: '/logs',
    name: 'Logs',
    icon: 'info',
    component: () => lazyLoadView(import('@views/pages/settings/Logs')),
    meta: { authRequired: true },
  },
];

const userRoutes = [
  {
    path: '/users',
    name: 'Users',
    icon: 'user',
    component: () => lazyLoadView(import('@views/pages/settings/Users')),
    meta: { authRequired: true },
  },
];

const userUrlRoutes = [
  {
    path: '/user',
    name: 'User Url',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: '/view/:id',
        name: 'User View',
        component: () => lazyLoadView(import('@views/pages/settings/UserView')),
      },
      {
        path: 'add-new',
        name: 'User Add',
        component: () => lazyLoadView(import('@views/pages/settings/UserAddNew')),
      },
      {
        path: '/edit/:id',
        name: 'User Edit',
        component: () => lazyLoadView(import('@views/pages/settings/UserEdit')),
      }
    ],
  },
];

const meUrlRoutes = [
  {
    path: '/me',
    name: 'ME Url',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'view',
        name: 'Me View',
        component: () => lazyLoadView(import('@views/pages/me/MyProfile')),
      },
      {
        path: 'update',
        name: 'Update Me',
        component: () => lazyLoadView(import('@views/pages/me/UpdateProfile')),
      }
    ],
  },
];

const authProtectedRoutes = [
  ...dashboardRoutes,
  ...operationsRoutes,
  ...technicalRoutes,
  ...prlogisticRoutes,
  ...hseRoutes,
  ...crewingRoutes,
  ...reportRoutes,
  ...settingRoutes,
  ...userRoutes,
  ...logRoutes,
]
// If you want don't show to menu, register your routes here
const allRoutes = [...authRoutes, ...authProtectedRoutes, ...errorPagesRoutes, ...operationalRoutes, ...userUrlRoutes, ...meUrlRoutes ]

export { allRoutes, authProtectedRoutes }

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@components/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    // error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
